import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Lock, MoreHorizontal, Plus, RefreshCw, Upload } from 'lucide-react';
import Tooltip from './ui/Tooltip';
import ContextMenu from './ui/ContextMenu';

/**
 * The Vault's page actions, as a "⋯" menu next to the vault search (owner,
 * 20.10.2026: the actions leave the title bar, where they "look terrible").
 * Each row dispatches the same window event the title bar used to, so
 * VaultView handles them unchanged: vault-new-item, vault-import-env,
 * vault-import, vault-export, vault-refresh, vault-lock.
 */
export const VAULT_ACTIONS = Object.freeze([
  { id: 'new-item', event: 'vault-new-item', key: 'vault.actions.newItem', Icon: Plus },
  { id: 'import-env', event: 'vault-import-env', key: 'vault.actions.importEnv', Icon: Download },
  { id: 'import', event: 'vault-import', key: 'vault.actions.importFile', Icon: Download },
  { id: 'export', event: 'vault-export', key: 'vault.actions.export', Icon: Upload },
  { id: 'refresh', event: 'vault-refresh', key: 'vault.actions.refresh', Icon: RefreshCw, separator: true },
  { id: 'lock', event: 'vault-lock', key: 'vault.actions.lock', Icon: Lock },
]);

export default function VaultActionsMenu() {
  const { t } = useTranslation();
  const ref = useRef(null);
  const [menu, setMenu] = useState(null);

  const open = () => {
    const r = ref.current?.getBoundingClientRect();
    if (r) setMenu({ x: r.right, y: r.bottom + 4 });
  };

  const items = VAULT_ACTIONS.flatMap((a) => [
    ...(a.separator ? [{ separator: true }] : []),
    { icon: <a.Icon size={14} />, label: t(a.key), onClick: () => window.dispatchEvent(new CustomEvent(a.event)) },
  ]);

  return (
    <>
      <Tooltip content={t('vault.actions.menu')} side="bottom">
        <button
          ref={ref}
          type="button"
          className="vault-actions-btn"
          aria-label={t('vault.actions.menu')}
          aria-haspopup="menu"
          aria-expanded={!!menu}
          data-testid="vault-actions"
          onClick={() => (menu ? setMenu(null) : open())}
        >
          <MoreHorizontal size={15} aria-hidden />
        </button>
      </Tooltip>
      {menu && (
        <ContextMenu x={menu.x} y={menu.y} align="end" keyboard label={t('vault.actions.menu')} onClose={() => setMenu(null)} items={items} />
      )}
    </>
  );
}
